import CloseIcon from '@mui/icons-material/Close'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import MenuIcon from '@mui/icons-material/Menu'
import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { snakeCase } from 'lodash'
import { Fragment, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom-v5-compat'
import { companyLinkActive } from 'shared/administration/navigation'
import { FlexBox, Grid } from 'shared/components/Base/Base'
import { Text } from 'shared/components/Text/Text'
import { useBreakpoint } from 'shared/hooks/useBreakpoint'
import { breakpoints } from 'shared/theme/breakpoints'
import { Colors } from 'shared/theme/colors'
import { label3 } from 'shared/theme/typography'
import styled, { css } from 'styled-components'

import { useAnalyticsEvent } from '../../../hooks/useAnalyticsEvent'
import { useLayoutContext } from '../../Context/LayoutContext'
import { SideBarFooter } from './SideBarFooter'
import { SideBarHeader } from './SideBarHeader'
import { useShowSideBar } from './useShowSideBar'
import { SideBarMenuItem, useSideBarMenuItems } from './useSideBarMenuItems'
import { useSidebarWidth } from './useSideBarWidth'

export const SIDEBAR_EXPANDED_WIDTH = 230
export const SIDEBAR_COLLAPSED_WIDTH = 64
export const SIDEBAR_PADDING = 12

export function SideBar() {
  const { isSideBarCollapsed, setIsSideBarCollapsed, fixNavToTop } = useLayoutContext()
  const currentPage = useLocation().pathname
  const sidebarMenuItems = useSideBarMenuItems()
  const sidebarWidth = useSidebarWidth()
  const showSidebar = useShowSideBar()
  const isXsMobile = useBreakpoint('xs')
  const trackEvent = useAnalyticsEvent()
  const mobileMenuRef = useRef<HTMLButtonElement>(null)
  const collapseRef = useRef<HTMLButtonElement>(null)
  const moveMobileFocus = useRef(false)

  useEffect(() => {
    if (!moveMobileFocus.current) return
    moveMobileFocus.current = false
    if (showSidebar && isXsMobile) {
      const target = isSideBarCollapsed ? mobileMenuRef.current : collapseRef.current
      target?.focus()
    }
  }, [isSideBarCollapsed, isXsMobile, showSidebar])

  if (!showSidebar) return null

  function matchesCurrentPage(page: string, pages?: string[]) {
    if (page.startsWith('/manage/')) {
      const base = page.match(/^\/manage(?:\/company-support\/[^/]+)?/)?.[0] || '/manage'
      return companyLinkActive(currentPage.slice(base.length), page.slice(base.length))
    }
    return Boolean(
      currentPage === page.replace(/\/$/, '') ||
        currentPage.includes(page) ||
        pages?.some((p) => currentPage.includes(p))
    )
  }

  function closeMobileNavigation() {
    moveMobileFocus.current = true
    setIsSideBarCollapsed(true)
  }

  function handleNavItemClick(item: SideBarMenuItem) {
    trackEvent('nav_sidebar_click', { page_name: snakeCase(item.label) })
    if (item.external) return
    if (isXsMobile) {
      closeMobileNavigation()
    }
  }

  function handleCollapseClick() {
    trackEvent('nav_sidebar_collapse_click', { is_collapsing: !isSideBarCollapsed })
    if (isXsMobile) closeMobileNavigation()
    else setIsSideBarCollapsed(!isSideBarCollapsed)
  }

  function handleMobileCollapseClick() {
    trackEvent('nav_sidebar_mobile_collapse_click', { is_collapsing: !isSideBarCollapsed })
    moveMobileFocus.current = true
    setIsSideBarCollapsed(false)
  }

  return (
    <>
      <SideBarWrap
        id="app-sidebar"
        $isSideBarCollapsed={isSideBarCollapsed}
        hidden={isXsMobile && isSideBarCollapsed}
        onKeyDown={(event) => {
          if (isXsMobile && event.key === 'Escape') {
            event.preventDefault()
            closeMobileNavigation()
          }
        }}
      >
        <HeaderRow $isSideBarCollapsed={isSideBarCollapsed}>
          <HeaderBrand $isSideBarCollapsed={isSideBarCollapsed}>
            <KeepWidth>
              <SideBarHeader />
            </KeepWidth>
          </HeaderBrand>
          <CollapseButton
            ref={collapseRef}
            aria-label={
              isXsMobile
                ? 'Close navigation'
                : isSideBarCollapsed
                  ? 'Expand navigation'
                  : 'Collapse navigation'
            }
            aria-expanded={!isSideBarCollapsed}
            aria-controls="app-sidebar"
            onClick={handleCollapseClick}
          >
            {isXsMobile ? (
              <CloseIcon fontSize="small" />
            ) : isSideBarCollapsed ? (
              <KeyboardDoubleArrowRightIcon fontSize="small" />
            ) : (
              <KeyboardDoubleArrowLeftIcon fontSize="small" />
            )}
          </CollapseButton>
        </HeaderRow>
        <SectionDivider $hidden={isSideBarCollapsed} />
        <SideNav aria-label="Main navigation" $isSideBarCollapsed={isSideBarCollapsed}>
          {sidebarMenuItems.map((section, index) => {
            return (
              <Fragment key={section[0].label}>
                {index > 0 && <SectionDivider />}
                {section.map((item) => {
                  const isActive = matchesCurrentPage(item.url, item.activeUrls)
                  return (
                    <NavItemLink
                      data-testid={snakeCase(item.label)}
                      key={item.label}
                      to={item.url}
                      aria-label={item.unreadCount ? `${item.label}, ${item.unreadCount} unread` : item.label}
                      aria-current={isActive ? 'page' : undefined}
                      reloadDocument={item.external}
                      onClick={() => handleNavItemClick(item)}
                    >
                      <KeepWidth>
                        <Grid columns="20px auto" alignItems="center" gap="12px">
                          <MenuTooltip
                            title={
                              isSideBarCollapsed ? (
                                <TooltipContent>
                                  {item.label}
                                  {item.unreadCount ? `, ${item.unreadCount} unread` : ''}
                                </TooltipContent>
                              ) : null
                            }
                            placement="right"
                          >
                            <FlexBox styles="width: 20px;">
                              <Badge
                                color="primary"
                                variant="dot"
                                invisible={!isSideBarCollapsed || !item.unreadCount}
                              >
                                <NavItemIcon as={item.icon} $isActive={isActive} />
                              </Badge>
                            </FlexBox>
                          </MenuTooltip>
                          <NavItemLinkText
                            variant="header3"
                            $isActive={isActive}
                            $isCollapsed={isSideBarCollapsed}
                            aria-hidden="true"
                          >
                            <span className="min-w-0 flex-1 truncate">{item.label}</span>
                            {!!item.unreadCount && <UnreadCount>{item.unreadCount}</UnreadCount>}
                          </NavItemLinkText>
                        </Grid>
                      </KeepWidth>
                    </NavItemLink>
                  )
                })}
              </Fragment>
            )
          })}
        </SideNav>
        <KeepWidth>
          <SideBarFooter />
        </KeepWidth>
      </SideBarWrap>
      {sidebarWidth === 0 && isSideBarCollapsed ? (
        <MobileMenuButton
          ref={mobileMenuRef}
          aria-label="Open navigation"
          aria-expanded={!isSideBarCollapsed}
          aria-controls="app-sidebar"
          onClick={handleMobileCollapseClick}
          $fixNavToTop={fixNavToTop}
        >
          <MenuIcon />
        </MobileMenuButton>
      ) : null}
    </>
  )
}

const SideBarWrap = styled.div<{ $isSideBarCollapsed?: boolean }>`
  width: ${SIDEBAR_EXPANDED_WIDTH}px;
  background: ${Colors.white};
  border-right: 1px solid ${Colors.lightGray4};
  box-sizing: border-box;
  position: fixed;
  z-index: 9;
  top: 0;
  bottom: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 10px ${SIDEBAR_PADDING}px 24px;
  display: flex;
  flex-direction: column;

  &[hidden] {
    display: none;
  }

  ${(p) =>
    p.$isSideBarCollapsed &&
    css`
      width: ${SIDEBAR_COLLAPSED_WIDTH}px;
    `}

  @media ${breakpoints.xs} {
    width: ${SIDEBAR_EXPANDED_WIDTH}px;
    box-shadow: 8px 0 24px rgba(16, 16, 19, 0.12);
  }
`

const SideNav = styled.nav<{ $isSideBarCollapsed?: boolean }>`
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  ${(p) =>
    p.$isSideBarCollapsed &&
    css`
      width: ${SIDEBAR_COLLAPSED_WIDTH - SIDEBAR_PADDING * 2}px;
    `}
`

const SectionDivider = styled.div<{ $hidden?: boolean }>`
  height: 1px;
  flex-shrink: 0;
  margin: 9px 4px 8px;
  background: ${Colors.lightGray4};

  ${(p) =>
    p.$hidden &&
    css`
      visibility: hidden;
      margin: 2px 4px;
    `}
`

const NavItemIcon = styled.div<{ $isActive: boolean }>`
  color: ${Colors.lightGray1};
  transition: color 0.2s !important;

  && {
    font-size: 20px;
  }

  ${(p) =>
    p.$isActive &&
    css`
      color: ${Colors.royalBlue};
    `}
`

const NavItemLinkText = styled(Text)<{ $isActive: boolean; $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  margin-top: 2px;
  color: ${Colors.darkGray};
  font-weight: 500;
  transition:
    opacity 0.2s,
    color 0.2s;

  ${(p) =>
    p.$isActive &&
    css`
      color: ${Colors.royalBlue};
      font-weight: 600;
    `}

  ${(p) =>
    p.$isCollapsed &&
    css`
      opacity: 0;
    `}
`

const UnreadCount = styled.span`
  margin-left: 8px;
  padding: 0 7px;
  border-radius: 999px;
  background: ${Colors.royalBlue};
  color: ${Colors.white};
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  font-variant-numeric: tabular-nums;
`

const NavItemLink = styled(Link)`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 40px;
  padding: 4px 10px;
  margin-top: 2px;
  text-decoration: none;
  color: ${Colors.darkGray};
  border-radius: 8px;
  transition: background-color 0.15s;

  &:hover {
    background: ${Colors.bgGray};
  }

  &[aria-current='page'] {
    background: rgba(0, 44, 177, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.royalBlue};
    outline-offset: -2px;
  }
`

const CollapseButton = styled(IconButton)`
  color: ${Colors.lightGray1};
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  padding: 6px;
  border-radius: 8px;

  &:hover {
    color: ${Colors.darkGray};
    background: ${Colors.bgGray};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.royalBlue};
  }
`

const MobileMenuButton = styled(IconButton)<{ $fixNavToTop?: boolean }>`
  position: absolute;
  z-index: 8;
  color: ${Colors.darkGray};
  width: 40px;
  height: 40px;
  margin: 12px 0 0 4px;
  border-radius: 8px;

  ${(p) =>
    p.$fixNavToTop &&
    css`
      position: fixed;
    `}

  &:hover {
    color: ${Colors.primaryBlack};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.royalBlue};
  }
`

const MenuTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: Colors.white,
    border: `1px solid ${Colors.lightGray4}`,
    boxShadow: '0 4px 12px rgba(16, 16, 19, 0.1)',
  },
}))

const TooltipContent = styled.div`
  ${label3};
  color: ${Colors.darkGray};
  padding: 1px 5px 0;
`

const KeepWidth = styled.div`
  width: ${SIDEBAR_EXPANDED_WIDTH - SIDEBAR_PADDING * 2}px;
`

/* Company identity and the collapse control share one line; the rail stacks them. */
const HeaderRow = styled.div<{ $isSideBarCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;

  ${(p) =>
    p.$isSideBarCollapsed &&
    css`
      flex-direction: column-reverse;
      justify-content: flex-end;
    `}
`

const HeaderBrand = styled.div<{ $isSideBarCollapsed?: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;

  ${(p) =>
    p.$isSideBarCollapsed &&
    css`
      flex: none;
      width: 100%;

      ${KeepWidth} {
        width: auto;
      }
    `}
`
